// /app/brands — Brand list. Restyled to handoff inline-style +
// tokens.css utility classes; uses BrandCard pattern from home.jsx.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useBrands } from "@/hooks/use-persona";
import { useActiveBrand } from "@/stores/active-brand";
import { Icons, Rosette } from "@/components/icons";
import { BrandMark, PlatformChip, PageHead } from "@/components/primitives";
import type { Brand } from "@/lib/types";

export const Route = createFileRoute("/app/brands/")({
  component: BrandsList,
});

function BrandsList() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const navigate = useNavigate();
  const { data, isLoading } = useBrands();
  const brands = data?.data ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? "02 · Markalar" : "02 · Brands"}
        title={tr ? `${brands.length} marka.` : `${brands.length} brand${brands.length === 1 ? "" : "s"}.`}
        subtitle={tr
          ? "Workspace'inde yönettiğin tüm markalar. Bir karta tıkla, marka paneline gir."
          : "Every brand in your workspace. Click a card to enter the brand cockpit."}
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => navigate({ to: "/app/brands/new" })}>
            <Icons.Plus size={13} /> {tr ? "Yeni marka" : "New brand"}
          </button>
        }
      />

      <div className="scroll-y" style={{ flex: 1, padding: "24px 32px 48px" }}>
        {isLoading ? (
          <SkeletonGrid />
        ) : brands.length === 0 ? (
          <EmptyState />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
            {brands.map((b) => <Card key={b.id} brand={b} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ brand }: { brand: Brand }) {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const navigate = useNavigate();
  const { setActive } = useActiveBrand();
  const accentSoft = brand.accent_soft_hex ?? "rgba(224,45,28,0.12)";
  const postsWeek = brand.posts_this_week ?? 0;
  const pending = brand.pending_count ?? 0;
  // Removed synthetic [4,6,5,9,8,11,...] sparkline — was lying to the
  // operator (same fake series on every brand). Will re-add once
  // brand.timeseries field ships from backend with real daily counts.
  return (
    <div
      className="card card-int"
      onClick={() => {
        setActive(brand);
        void navigate({ to: "/app/brands/$brandId", params: { brandId: brand.id } });
      }}
      style={{
        padding: 18,
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 100,
          height: 100,
          background: `radial-gradient(circle at top right, ${accentSoft}, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
        <BrandMark brand={brand} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}>{brand.trade_name}</div>
          <div className="mono" style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.05em" }}>{brand.industry ?? "—"}</div>
        </div>
        <span className="btn-icon" onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
          <Icons.More size={14} />
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div className="mono" style={{ fontSize: 9, color: "var(--mu)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
            {tr ? "Bu hafta" : "This week"}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span className="t-num" style={{ fontSize: 22 }}>{postsWeek}</span>
            <span style={{ fontSize: 11, color: "var(--tx2)" }}>{tr ? "gönderi" : "posts"}</span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {(brand.connected_platforms ?? []).map((pl) => <PlatformChip key={pl} platform={pl} size={18} />)}
        <div style={{ flex: 1 }} />
        {pending > 0 ? (
          <span className="badge badge-amber"><span className="dot" />{pending} {tr ? "bekliyor" : "pending"}</span>
        ) : (
          <span className="badge badge-green"><span className="dot" />{tr ? "Temiz" : "Clear"}</span>
        )}
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
      {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skel" style={{ height: 180, borderRadius: 12 }} />)}
    </div>
  );
}

function EmptyState() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const navigate = useNavigate();
  return (
    <div className="card" style={{ padding: "64px 24px", textAlign: "center" }}>
      <Rosette size={64} stroke="var(--accent)" opacity={0.5} />
      <div className="eyebrow" style={{ marginTop: 18, marginBottom: 10, justifyContent: "center" }}>
        {tr ? "Boş workspace" : "Empty workspace"}
      </div>
      <div style={{ fontFamily: "var(--display)", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
        {tr ? "İlk markanı ekle" : "Add your first brand"}
      </div>
      <div style={{ color: "var(--tx2)", fontSize: 13, maxWidth: 380, margin: "0 auto 16px" }}>
        {tr
          ? "Markanı bir paragrafta tanıt — AI sektörünü, ses tonunu, hedef kitleyi ve içerik konularını taslak hâlinde hazırlar. 5 dakikadan kısa sürer."
          : "Describe your brand in a paragraph — AI drafts industry, tone, target audience and content topics. Takes under 5 minutes."}
      </div>
      <button className="btn btn-primary btn-lg" onClick={() => navigate({ to: "/app/brands/new" })}>
        <Icons.Plus size={14} /> {tr ? "Yeni marka" : "New brand"}
      </button>
    </div>
  );
}
